import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuditLogs } from '../../hooks/use-admin';
import {
  Shield,
  Filter,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  FileText,
  Ban,
  Eye,
  Trash2,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';

const CATEGORY_INFO = {
  user_moderation: {
    label: 'User Moderation',
    icon: User,
    color: 'text-orange-400',
  },
  content_moderation: {
    label: 'Content Moderation',
    icon: FileText,
    color: 'text-blue-400',
  },
  report_handling: {
    label: 'Report Handling',
    icon: AlertCircle,
    color: 'text-yellow-400',
  },
  system: { label: 'System', icon: Shield, color: 'text-purple-400' },
  authentication: {
    label: 'Authentication',
    icon: Shield,
    color: 'text-green-400',
  },
};

const ACTION_ICONS = {
  warn: AlertTriangle,
  suspend: Clock,
  ban: Ban,
  unban: CheckCircle,
  hide: Eye,
  restore: RefreshCw,
  delete: Trash2,
  resolve: CheckCircle,
  dismiss: XCircle,
};

const SEVERITY_BADGES = {
  low: {
    label: 'Low',
    className: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  },
  medium: {
    label: 'Medium',
    className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  },
  high: {
    label: 'High',
    className: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  },
  critical: {
    label: 'Critical',
    className: 'bg-red-500/20 text-red-400 border-red-500/30',
  },
};

function AuditLogRow({ log, onClick }) {
  const categoryInfo = CATEGORY_INFO[log.category] || CATEGORY_INFO.system;
  const CategoryIcon = categoryInfo.icon;
  const ActionIcon = ACTION_ICONS[log.action] || Shield;
  const severityInfo = SEVERITY_BADGES[log.severity] || SEVERITY_BADGES.medium;

  return (
    <div
      className="flex items-start justify-between p-4 border-b border-zinc-800 hover:bg-zinc-900/50 transition-colors cursor-pointer"
      onClick={() => onClick(log)}
    >
      <div className="flex items-start gap-4 flex-1">
        <div className={cn('p-2 rounded-lg bg-zinc-800', categoryInfo.color)}>
          <CategoryIcon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-white font-medium capitalize">
              {log.action?.replace(/_/g, ' ')}
            </span>
            <Badge variant="outline" className={severityInfo.className}>
              {severityInfo.label}
            </Badge>
            {log.success ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                Success
              </Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                Failed
              </Badge>
            )}
          </div>

          <p className="text-sm text-zinc-400 mb-2">
            {log.details?.description || `${log.action} action performed`}
          </p>

          <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {log.adminWallet?.slice(0, 8)}...{log.adminWallet?.slice(-4)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(log.timestamp).toLocaleString()}
            </span>
            {log.requestIP && (
              <span className="text-zinc-600">IP: {log.requestIP}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminAuditLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedLog, setSelectedLog] = useState(null);

  const currentPage = parseInt(searchParams.get('page') || '1');
  const categoryFilter = searchParams.get('category') || 'all';
  const severityFilter = searchParams.get('severity') || 'all';
  const actionFilter = searchParams.get('action') || 'all';

  const { data, isLoading, error } = useAdminAuditLogs({
    page: currentPage,
    limit: 30,
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    severity: severityFilter !== 'all' ? severityFilter : undefined,
    action: actionFilter !== 'all' ? actionFilter : undefined,
  });

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Audit Logs
        </h1>
        <p className="text-zinc-400">
          Track all administrative actions and system events
        </p>
      </div>

      {/* Filters */}
      <Card className="border border-zinc-800 bg-zinc-900/50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select
              value={categoryFilter}
              onValueChange={(v) => handleFilterChange('category', v)}
            >
              <SelectTrigger className="w-full md:w-[180px] bg-zinc-800 border-zinc-700 text-white">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="user_moderation">User Moderation</SelectItem>
                <SelectItem value="content_moderation">
                  Content Moderation
                </SelectItem>
                <SelectItem value="report_handling">Report Handling</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="authentication">Authentication</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={severityFilter}
              onValueChange={(v) => handleFilterChange('severity', v)}
            >
              <SelectTrigger className="w-full md:w-[150px] bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={actionFilter}
              onValueChange={(v) => handleFilterChange('action', v)}
            >
              <SelectTrigger className="w-full md:w-[150px] bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="warn">Warn</SelectItem>
                <SelectItem value="suspend">Suspend</SelectItem>
                <SelectItem value="ban">Ban</SelectItem>
                <SelectItem value="unban">Unban</SelectItem>
                <SelectItem value="hide">Hide</SelectItem>
                <SelectItem value="restore">Restore</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="resolve">Resolve</SelectItem>
                <SelectItem value="dismiss">Dismiss</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs List */}
      <Card className="border border-zinc-800 bg-zinc-900/50 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading audit logs...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-4" />
            <p className="text-red-400">{error.message}</p>
          </div>
        ) : data?.logs?.length === 0 ? (
          <div className="p-8 text-center">
            <Shield className="h-8 w-8 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No audit logs found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-zinc-800">
              {data?.logs?.map((log) => (
                <AuditLogRow
                  key={log._id || log.logId}
                  log={log}
                  onClick={setSelectedLog}
                />
              ))}
            </div>

            {/* Pagination */}
            {data?.pagination && (
              <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
                <p className="text-sm text-zinc-400">
                  Showing {(currentPage - 1) * 30 + 1} to{' '}
                  {Math.min(currentPage * 30, data.pagination.total)} of{' '}
                  {data.pagination.total} logs
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm text-zinc-400 flex items-center">
                    Page {currentPage} of {data.pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= data.pagination.pages}
                    className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Log Details Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-400" />
              Audit Log Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500">Log ID</label>
                  <p className="text-sm text-white font-mono">
                    {selectedLog.logId || selectedLog._id}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Timestamp</label>
                  <p className="text-sm text-white">
                    {new Date(selectedLog.timestamp).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Category</label>
                  <p className="text-sm text-white capitalize">
                    {selectedLog.category?.replace(/_/g, ' ')}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Action</label>
                  <p className="text-sm text-white capitalize">
                    {selectedLog.action?.replace(/_/g, ' ')}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Severity</label>
                  <Badge
                    variant="outline"
                    className={SEVERITY_BADGES[selectedLog.severity]?.className}
                  >
                    {selectedLog.severity}
                  </Badge>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Status</label>
                  {selectedLog.success ? (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      Success
                    </Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      Failed
                    </Badge>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-500">Admin Wallet</label>
                <p className="text-sm text-white font-mono">
                  {selectedLog.adminWallet}
                </p>
              </div>

              {selectedLog.requestIP && (
                <div>
                  <label className="text-xs text-zinc-500">Request IP</label>
                  <p className="text-sm text-white font-mono">
                    {selectedLog.requestIP}
                  </p>
                </div>
              )}

              {selectedLog.targetResources &&
                selectedLog.targetResources.length > 0 && (
                  <div>
                    <label className="text-xs text-zinc-500">
                      Target Resources
                    </label>
                    <div className="mt-1 bg-zinc-800 rounded-lg p-3 space-y-2">
                      {selectedLog.targetResources.map((resource, i) => (
                        <div key={i} className="text-sm">
                          <span className="text-zinc-400 capitalize">
                            {resource.type}:{' '}
                          </span>
                          <span className="text-white font-mono">
                            {resource.id}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {selectedLog.details &&
                Object.keys(selectedLog.details).length > 0 && (
                  <div>
                    <label className="text-xs text-zinc-500">Details</label>
                    <pre className="mt-1 bg-zinc-800 rounded-lg p-3 text-sm text-zinc-300 overflow-x-auto">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}

              {selectedLog.changes && (
                <div>
                  <label className="text-xs text-zinc-500">Changes</label>
                  <div className="mt-1 bg-zinc-800 rounded-lg p-3 space-y-2">
                    {selectedLog.changes.before && (
                      <div>
                        <span className="text-xs text-zinc-500">Before:</span>
                        <pre className="text-sm text-red-400 overflow-x-auto">
                          {JSON.stringify(selectedLog.changes.before, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.changes.after && (
                      <div>
                        <span className="text-xs text-zinc-500">After:</span>
                        <pre className="text-sm text-green-400 overflow-x-auto">
                          {JSON.stringify(selectedLog.changes.after, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedLog.error && (
                <div>
                  <label className="text-xs text-zinc-500">Error</label>
                  <p className="text-sm text-red-400 bg-red-500/10 p-3 rounded-lg mt-1">
                    {selectedLog.error}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setSelectedLog(null)}
              className="text-zinc-400 hover:text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
